import React, {useEffect, useMemo, useRef, useState} from "react";
import FieldTimeDropdown from "./field-time-dropdown";
import moment from "moment";
import {classNames} from "../../../util/util-helpers";
import FieldTimeFocusElement from "./field-time-focus-element";

const periodOptions = ["AM", "PM"];

const minutesOptions = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

const inputClass = "w-6 h-6 p-0 text-sm text-center border-none focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-0.5 rounded-xs bg-field text-tm-gray-700 disabled:bg-transparent disabled:text-tm-gray-400 disabled:cursor-not-allowed";

export function formatValue(hours, minutes, period, timeFormat) {
    if (!hours || !minutes) {
        return "";
    }

    const time = timeFormat === "h:mm A"
        ? hours + ":" + minutes + " " + period
        : hours + ":" + minutes;

    return moment(time, timeFormat).format("HH:mm:ss");
}

export default function FieldTimeCustomNew({onChange, name, value, timeFormat, withDateField, disabled, errorMessage, addClass, dropdownClass, dropUp}) {
    if (!["h:mm A", "HH:mm"].includes(timeFormat)) {
        timeFormat = "h:mm A";
    }

    const { hoursOptions, MAX_HOURS } = useMemo(() => ({
        hoursOptions: timeFormat === "h:mm A"
            ? ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"]
            : ["00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23"],
        MAX_HOURS: timeFormat === "h:mm A" ? 12 : 23,
    }), [timeFormat]);

    const hasTimePeriod = timeFormat === "h:mm A";

    const inputsContainerRef = useRef(null);
    const hoursInputRef = useRef(null);
    const minutesInputRef = useRef(null);
    const periodInputRef = useRef(null);
    const dropdownRef = useRef(null);
    const wholeFieldRef = useRef(null);

    const hoursOptionsRef = useRef({});

    const [isWholeFieldFocused, setIsWholeFieldFocused] = useState(false);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [hoursValue, setHoursValue] = useState("");
    const [minutesValue, setMinutesValue] = useState("");
    const [periodValue, setPeriodValue] = useState(periodOptions[0]);
    function handleContainerClick() {
        hoursInputRef.current.focus();
    }

    function handleOnFocus() {
        setIsDropdownVisible(true);
        setIsWholeFieldFocused(true);
    }

    function setValue(e) {
        let value = e.target.value;
        value = value.replace(/[^0-9]/g, '');

        if (value.length > 2) {
            value = value.slice(2);
        }

        return value;
    }

    function handleHourChange(e) {
        let value = setValue(e);

        setHoursValue(value);
    }

    function handleMinutesChange(e) {
        let value = setValue(e);

        setMinutesValue(value);
    }

    function handleMouseUp(e) {
        if (e.button === 0) {
            e.target.select()
        }
    }

    function handleHoursBlur(e) {
        if (!hoursValue) {
            return null;
        }

        if (Number(hoursValue) > MAX_HOURS) {
            handleSetValue("hours", "12");
        } else if (Number(hoursValue) < 0) {
            handleSetValue("hours", "00");
        } else if (hoursValue.length === 1 && !hasTimePeriod) {
            handleSetValue("hours", "0" + hoursValue);
        } else {
            handleSetValue("hours", e.target.value);
        }
    }

    function handleMinutesBlur(e) {

        if (!minutesValue) {
            return null;
        }

        if (Number(minutesValue) > 59) {
            handleSetValue("minutes", "00");
        } else if (Number(minutesValue) < 0) {
            handleSetValue("minutes", "00");
        } else if (minutesValue.length === 1) {
            handleSetValue("minutes", "0" + minutesValue);
        } else {
            handleSetValue("minutes", e.target.value);
        }
    }

    function handleUnFocusComponent() {
        handleChangeValue();
        setIsDropdownVisible(false);
        setIsWholeFieldFocused(false);
    }

    function handleChangeValue(h, m, p) {
        let hours = h ?? hoursValue;
        let minutes = m ?? minutesValue;
        let period = p ?? periodValue;

        if (h && !minutesValue) {
            minutes = minutesOptions[0];
        }

        if (m && !hoursValue) {
            hours = hoursOptions[0];
        }

        let formattedValue = formatValue(hours, minutes, period, timeFormat);

        const localIsEmpty = !hours || !minutes;
        const nextValue = localIsEmpty ? "" : formattedValue;

        if (nextValue === (value ?? "")) {
            return;
        }

        if (withDateField?.value && formattedValue) {
            const changeFieldName = withDateField?.name;
            onChange(changeFieldName, localIsEmpty ? "" : withDateField.value.split(" ")[0] + " " + formattedValue);
        }

        onChange(name, nextValue);
    }


    function onHourInputKeyDown(e) {
        const currentValueIndex = hoursOptions.indexOf(hoursValue);

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            let selectedHour = hoursOptions[currentValueIndex - 1]
                ? currentValueIndex - 1 // Go up
                : hoursOptions.length - 1; // Go to the end

            handleSetValue("hours", hoursOptions[selectedHour]);
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();

            const selectedHour = hoursOptions[currentValueIndex + 1]
                ? currentValueIndex + 1 // Go down
                : 0; // Go to the start
            handleSetValue("hours", hoursOptions[selectedHour]);
        }

        if (e.key === 'ArrowLeft') {
            e.preventDefault();
        }

        if (e.key === 'ArrowRight') {
            e.preventDefault();
            minutesInputRef.current.focus();
            minutesInputRef.current.select();
        }

        if (hasTimePeriod && (e.key === 'p' || e.key === 'P')) {
            e.preventDefault();
            handleSetValue("period", "PM");
        }

        if (hasTimePeriod && (e.key === 'a' || e.key === 'A')) {
            e.preventDefault();
            handleSetValue("period", "AM");
        }

        if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault()
            handleUnFocusComponent();
            wholeFieldRef.current.focus();
            setIsWholeFieldFocused(true);
        }

        if (e.key === 'Tab' && e.shiftKey) {
            handleUnFocusComponent();
        }
    }

    function onMinuteInputKeyDown(e) {
        const currentValueIndex = minutesOptions.indexOf(minutesValue);

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            const selectedMinute = currentValueIndex - 1 !== -1 && currentValueIndex - 1 !== -2
                ? currentValueIndex - 1  // Go up
                : minutesOptions.length - 1; // Go to the end
            handleSetValue("minutes", minutesOptions[selectedMinute]);
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const selectedMinute = currentValueIndex + 1 !== -1 && minutesOptions[currentValueIndex + 1]
                ? currentValueIndex + 1// Go down
                : 0; // Go to the start

            handleSetValue("minutes", minutesOptions[selectedMinute]);
        }

        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            hoursInputRef.current.focus();
            hoursInputRef.current.select();
        }

        if (e.key === 'ArrowRight' && hasTimePeriod) {
            e.preventDefault();
            periodInputRef.current.focus();
            periodInputRef.current.select();
        }

        if (!hasTimePeriod && e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            handleUnFocusComponent();
            focusAfterComponent();
        }

        if (hasTimePeriod && (e.key === 'p' || e.key === 'P')) {
            e.preventDefault();
            handleSetValue("period", "PM");
        }

        if (hasTimePeriod && (e.key === 'a' || e.key === 'A')) {
            e.preventDefault();
            handleSetValue("period", "AM");
        }

        if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault()
            handleUnFocusComponent();
            wholeFieldRef.current.focus();
            setIsWholeFieldFocused(true);
        }
    }

    function onPeriodInputKeyDown(e) {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            handleSetValue("period", periodValue === "AM" ? "PM" : "AM");
        }

        if (e.key === 'p' || e.key === 'P') {
            handleSetValue("period", "PM");
        }

        if (e.key === 'a' || e.key === 'A') {
            handleSetValue("period", "AM");
        }

        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            minutesInputRef.current.focus();
            minutesInputRef.current.select();
        }

        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            handleUnFocusComponent();
            focusAfterComponent();
        }

        if (e.key === 'ArrowRight') {
            e.preventDefault();
        }

        if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault()
            handleUnFocusComponent();
            wholeFieldRef.current.focus();
            setIsWholeFieldFocused(true);
        }
    }

    function handleSetValue(field, newValue) {
        if (field === "hours") {
            setHoursValue(newValue);
            handleChangeValue(newValue, undefined, undefined);
        } else if (field === "minutes") {
            setMinutesValue(newValue);
            handleChangeValue(undefined, newValue, undefined);
        } else if (field === "period") {
            setPeriodValue(newValue);
            handleChangeValue(undefined, undefined, newValue);
        }
    }

    function focusAfterComponent() {
        const anchor = hasTimePeriod ? periodInputRef.current : minutesInputRef.current;
        if (!anchor) return;
        setTimeout(() => {
            const focusable = [...document.querySelectorAll(
                'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"])'
            )];
            const idx = focusable.indexOf(anchor);
            if (idx !== -1 && focusable[idx + 1]) {
                focusable[idx + 1].focus();
            }
        }, 0);
    }

    const handleBlur = (e) => {
        const nextFocusedElement = e.relatedTarget;

        if (inputsContainerRef.current?.contains(nextFocusedElement)) {
            return;
        }

        if (dropdownRef.current && dropdownRef.current.contains(nextFocusedElement)) {
            return;
        }

        handleUnFocusComponent();
    };

    useEffect(() => {
        if (value && moment(value, "HH:mm:ss").isValid()) {
            const timeValue = moment(value, "HH:mm:ss").format(timeFormat).split(" ");
            const period = timeValue[1];
            let hours = timeValue[0].split(":")[0];
            const minutes = timeValue[0].split(":")[1];

            if (hours.length === 1) {
                hours = "0" + hours;
            }

            setHoursValue(hours);
            setMinutesValue(minutes);
            setPeriodValue(period);
        } else if (!value) {
            setHoursValue("");
            setMinutesValue("");
        }
    }, [value, timeFormat]);

    /** Render
     ================================================================= */
    return (
        <div
            className="relative w-full"
        >
            <div
                className={
                    classNames(
                        errorMessage?.length ? "border-red-600" : undefined,
                        isWholeFieldFocused ? "border-primary" : "",
                        disabled ? "bg-stripes cursor-not-allowed" : "",
                        addClass ?? "form-control",
                        "flex justify-center items-center gap-0.5"
                    )}
                onBlur={handleBlur}
                onClick={handleContainerClick}
                ref={inputsContainerRef}
            >
                <input
                    ref={hoursInputRef}
                    className={inputClass}
                    placeholder="--"
                    value={hoursValue}
                    onFocus={handleOnFocus}
                    onBlur={handleHoursBlur}
                    onClick={e => e.stopPropagation()}
                    onMouseUp={handleMouseUp}
                    onKeyDown={onHourInputKeyDown}
                    onChange={handleHourChange}
                    disabled={disabled}
                />

                <span className="text-sm">:</span>

                <input
                    ref={minutesInputRef}
                    className={inputClass}
                    placeholder="--"
                    value={minutesValue}
                    onFocus={handleOnFocus}
                    onBlur={handleMinutesBlur}
                    onClick={e => e.stopPropagation()}
                    onMouseUp={handleMouseUp}
                    onKeyDown={onMinuteInputKeyDown}
                    onChange={handleMinutesChange}
                    disabled={disabled}
                />

                {hasTimePeriod && (
                    <input
                        ref={periodInputRef}
                        className={inputClass}
                        readOnly
                        value={periodValue}
                        onFocus={handleOnFocus}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={onPeriodInputKeyDown}
                        disabled={disabled}
                    />
                )}
            </div>

            {isDropdownVisible && (
                <FieldTimeDropdown
                    inputsContainerRef={inputsContainerRef}
                    wholeFieldRef={wholeFieldRef}
                    hoursOptionsRef={hoursOptionsRef}
                    hoursInputRef={hoursInputRef}
                    minutesInputRef={minutesInputRef}
                    periodInputRef={periodInputRef}
                    innerRef={dropdownRef}
                    hoursValue={hoursValue}
                    onSetHoursValue={(v) => handleSetValue("hours", v)}
                    minutesValue={minutesValue}
                    onSetMinutesValue={(v) => handleSetValue("minutes", v)}
                    hasTimePeriod={hasTimePeriod}
                    periodOptions={periodOptions}
                    periodValue={periodValue}
                    onSetPeriodValue={(v) => handleSetValue("period", v)}
                    hoursOptions={hoursOptions}
                    minutesOptions={minutesOptions}
                    hideDropdown={handleUnFocusComponent}
                    onBlur={(e) => {
                        handleBlur(e)
                    }}
                    dropdownClass={dropdownClass}
                    dropUp={dropUp}
                    setIsWholeFieldFocused={setIsWholeFieldFocused}
                />
            )}

            {(isWholeFieldFocused || isDropdownVisible) && (
                <FieldTimeFocusElement
                    isWholeFieldFocused={isWholeFieldFocused}
                    innerRef={wholeFieldRef}
                    dropdownRef={dropdownRef}
                    inputsContainerRef={inputsContainerRef}
                    hoursInputRef={hoursInputRef}
                    minutesInputRef={minutesInputRef}
                    periodInputRef={periodInputRef}
                    hideDropdown={handleUnFocusComponent}
                    onTabForward={focusAfterComponent}
                    onArrowUpDown={() => {
                        hoursInputRef.current.focus();
                    }}
                />
            )}
        </div>
    )
}

